import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  AppBar,
  Box,
  Button,
  Menu,
  MenuItem,
  Toolbar,
} from '@mui/material';
import { Logout, Person } from '@mui/icons-material';
import { StyledEngineProvider } from '@mui/styled-engine';
import { Box as CustomBox } from '@/components/common';
import Sidebar from './Sidebar';
import { LAYOUT_CONSTANTS } from './constants';
import { authAtom } from '@/atom/authAtom';
import { useLogout } from '@/hooks/auth.hook';
import { searchAtom } from '@/atom/searchAtom';
import { errorToast } from '@/common/toast';
import axios from 'axios';

/**
 * 메인 레이아웃 컴포넌트
 * 
 * 애플리케이션의 전체 레이아웃을 담당하며, 상단 AppBar와 사이드바 네비게이션을 포함합니다.
 * 
 * @example
 * <Layout>
 *   <Dashboard />
 * </Layout>
 */
const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAtomValue(authAtom);
  const [search, setSearch] = useAtom(searchAtom);
  const setAuthAtom = useSetAtom(authAtom);

  // 컴포넌트 마운트 상태 (SSR 대응)
  const [isMounted, setIsMounted] = useState(false);

  // 사용자 메뉴 앵커 엘리먼트 (드롭다운 메뉴 위치 지정)
  const [anchorEl, setAnchorEl] = useState(null);
  const isUserMenuOpen = Boolean(anchorEl);

  // 로그아웃 훅
  const logoutMutation = useLogout();

  /**
   * 컴포넌트 마운트 시 실행
   */
  useEffect(() => {
    setIsMounted(true);
  }, []);

  /**
   * 인증 상태 초기화 함수
   * 로그아웃 시 또는 로그인 페이지 접근 시 호출됩니다.
   */
  const authReset = useCallback(() => {
    setAuthAtom({
      userId: '',
      username: '',
      role: 1,
      token: '',
      refreshToken: '',
    });
  }, [setAuthAtom]);

  /**
   * 로그인 페이지 접근 시 인증 상태 초기화
   */
  useEffect(() => {
    if (location.pathname === '/login') {
      authReset();
    }
  }, [location.pathname, authReset]);

  /**
   * 사용자 메뉴 열기/닫기 핸들러
   */
  const handleUserMenuClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  /**
   * 사용자 메뉴 닫기 핸들러
   */
  const handleUserMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  /**
   * 홈으로 이동 핸들러
   */
  const handleGoToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  /**
   * 로그아웃 핸들러
   * useLogout 훅을 사용하여 로그아웃을 처리하고, 성공/실패 여부와 관계없이 로그인 페이지로 이동합니다.
   */
  const handleLogout = useCallback(() => {
    logoutMutation.mutate(undefined, {
      onSuccess: (data) => {
        // 서버 응답이 성공이면 정상 처리
        if (data?.data?.code === 1) {
          // 성공 메시지는 표시하지 않음 (일반적인 UX)
          navigate('/login');
        } else {
          // 서버에서 실패 응답이 와도 클라이언트는 정리되었으므로 로그인 페이지로 이동
          errorToast('로그아웃에 실패했습니다.');
          navigate('/login');
        }
      },
      onError: (error) => {
        // 네트워크 오류 등으로 서버 요청이 실패해도 클라이언트는 정리되었으므로 로그인 페이지로 이동
        console.error('로그아웃 오류:', error);
        if (axios.isAxiosError(error)) {
          const axiosError = error;
          errorToast(axiosError.response?.data?.msg || '로그아웃 중 오류가 발생했습니다.');
        } else {
          errorToast('로그아웃 중 오류가 발생했습니다.');
        }
        navigate('/login');
      },
    });
  }, [navigate, logoutMutation]);

  /**
   * 페이지로 이동 핸들러
   * 검색 상태를 초기화하고 이동합니다.
   */
  const handleGoPage = useCallback(
    (path) => {
      setSearch('');
      navigate(path);
    },
    [navigate, setSearch]
  );

  /**
   * 사이드바 최소 높이 계산 (뷰포트 높이 - AppBar 높이)
   */
  const sidebarMinHeight = useMemo(
    () => `calc(100vh - ${LAYOUT_CONSTANTS.APP_BAR_HEIGHT}px)`,
    []
  );

  /**
   * 콘텐츠 영역 너비 계산 (전체 너비 - 사이드바 너비)
   */
  const contentWidth = useMemo(
    () => `calc(100% - ${LAYOUT_CONSTANTS.SIDEBAR_WIDTH}px)`,
    []
  );

  return (
    <>
      {isMounted && (
        <StyledEngineProvider injectFirst>
          {/* 메인 네비게이션 컨테이너 */}
          <Box sx={{ width: '100%', overflow: 'hidden' }} component='nav'>
            {/* 상단 AppBar */}
            <AppBar
              position='static'
              sx={{
                height: `${LAYOUT_CONSTANTS.APP_BAR_HEIGHT}px`,
                bgcolor:
                  'linear-gradient(90deg, rgba(138, 132, 232, 1) 0%, rgba(25, 118, 210, 1) 35%, rgba(0, 212, 255, 1) 100%)',
              }}
            >
              <Toolbar variant='dense'>
                <StyledBox width='100%' height={`${LAYOUT_CONSTANTS.TOOLBAR_HEIGHT}px`}>
                  {/* 로고 영역 */}
                  <Title onClick={handleGoToHome}>
                    <img src='/logo-secolab.png' alt='secolab logo' />
                  </Title>

                  {/* 사용자 정보 버튼 */}
                  <Button
                    color='inherit'
                    size='small'
                    onClick={handleUserMenuClick}
                    sx={{ border: '1px solid #eee', padding: '5px 10px', height: `${LAYOUT_CONSTANTS.TOOLBAR_HEIGHT}px` }}
                  >
                    <b>
                      <Person sx={{ fontSize: 18, verticalAlign: 'text-bottom', mr: 0.5 }} />
                      {auth.username}
                    </b>
                    님
                  </Button>
                </StyledBox>
              </Toolbar>
            </AppBar>

            {/* 콘텐츠 영역 (사이드바 + 메인 콘텐츠) */}
            <ContentBox>
              {/* 사이드바 네비게이션 */}
              <Sidebar onNavigate={handleGoPage} minHeight={sidebarMinHeight} />

              {/* 메인 콘텐츠 영역 */}
              <ChildrenContainer $width={contentWidth}>{children}</ChildrenContainer>
            </ContentBox>
          </Box>
        </StyledEngineProvider>
      )}

      {/* 사용자 메뉴 드롭다운 */}
      <Menu
        anchorEl={anchorEl}
        open={isUserMenuOpen}
        onClose={handleUserMenuClose}
        MenuListProps={{
          'aria-labelledby': 'user-menu-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem
          onClick={handleLogout}
          sx={{ width: '120px', display: 'flex', justifyContent: 'center', columnGap: 0.5 }}
        >
          <MenuBox>
            로그아웃
            <Logout sx={{ fontSize: 18, ml: 0.5 }} />
          </MenuBox>
        </MenuItem>
      </Menu>
    </>
  );
};

/**
 * 로고 제목 스타일 컴포넌트
 */
const Title = styled.h1`
  max-width: 130px;
  width: auto;
  font-size: 1.2rem;
  cursor: pointer;

  img {
    width: 100%;
    padding-top: 8px;
    filter: brightness(1.5);
  }
`;

/**
 * AppBar 내부 컨테이너 스타일 컴포넌트
 * 로고와 사용자 정보 버튼을 가로로 배치합니다.
 */
const StyledBox = styled(CustomBox)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 100%;

  b {
    text-transform: capitalize;
    margin-right: 2px;
    display: inline-flex;
    align-items: center;
  }
`;

/**
 * 메인 콘텐츠 영역 스타일 컴포넌트
 * 사이드바와 메인 콘텐츠를 가로로 배치합니다.
 */
const ContentBox = styled(CustomBox)`
  flex-direction: row;
  align-self: center;

  .MuiPaper-root {
    position: relative;
    border-top-right-radius: unset;
    border-bottom-right-radius: unset;
  }

  a {
    text-decoration: none;
  }

  .MuiList-root .MuiSvgIcon-root {
    color: #000;
  }

  b {
    font-weight: 700;
  }

  .MuiListItemIcon-root {
    min-width: 47px;
  }
`;

/**
 * 메인 콘텐츠 컨테이너 스타일 컴포넌트
 * 페이지별 콘텐츠가 표시되는 영역입니다.
 */
const ChildrenContainer = styled(CustomBox)`
  width: ${({ $width }) => $width};
  padding: 10px;
  overflow: hidden;
`;

/**
 * 사용자 메뉴 박스 스타일 컴포넌트
 */
const MenuBox = styled.div`
  font-weight: 700;

  .MuiSvgIcon-root {
    padding: 2px;
  }
`;

export default Layout;
